'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useMemo, useRef, useState } from 'react'
import { SliderNavigation } from '@/components/sliderNavigation'
import { MarketingVideo } from '@/features/marketing-videos/marketing-video'
import { useViewport } from '@/utils/viewport'

export interface ICommonVideoItem {
    url: string
    description: string
    title: string
    thumbnail?: string
}

export function CommonVideos({
    items,
    aspectRatio,
}: {
    items: ICommonVideoItem[]
    aspectRatio: number
}) {
    const videoRefs = useRef<Record<string, HTMLVideoElement>>({})
    const videoContainerRef = useRef<HTMLDivElement>(null)

    const [playingIndex, setPlayingIndex] = useState<number | null>(null)

    const [testimonialIndex, setTestimonialIndex] = useState(0)

    const { width } = useViewport()

    const isDesktop = width >= 1280

    const isTablet = width >= 768

    const groupedItems = useMemo(() => {
        if (isDesktop) {
            return [items]
        }

        if (isTablet) {
            const doublePairedItems = items.reduce((acc, _, i) => {
                if (i % 2 === 0) acc.push([items[i], items[i + 1]])
                return acc
            }, [] as ICommonVideoItem[][])

            return doublePairedItems
        }

        const singlePairedItems = items.map((item) => [item])

        return singlePairedItems
    }, [items, isDesktop, isTablet])

    const handlePause = useCallback((index: number) => {
        setPlayingIndex(null)

        videoRefs.current[index]?.pause()
    }, [])

    const handleVideoState = useCallback(
        (index: number) => {
            if (index === playingIndex && videoRefs.current[index]?.played) {
                handlePause(playingIndex)
                return
            }

            setPlayingIndex(index)

            Object.entries(videoRefs.current).forEach(([videoRefIndex]) => {
                const videoRef = videoRefs.current[videoRefIndex]

                if (String(index) !== videoRefIndex) {
                    videoRef.pause()
                }

                if (String(index) === videoRefIndex) {
                    if (videoRef.paused) {
                        videoRef.play()
                    } else {
                        videoRef.pause()
                    }
                }
            })
        },
        [playingIndex, handlePause]
    )

    return (
        <>
            <div
                className="mb-8 flex gap-6 justify-center"
                ref={videoContainerRef}
                style={{
                    minHeight: videoContainerRef.current?.clientHeight ?? 0,
                }}
            >
                <AnimatePresence mode="wait">
                    {groupedItems[testimonialIndex]?.map((testimonial, index) => {
                        return (
                            <motion.div
                                key={`${testimonialIndex}+${testimonial.title}`}
                                initial={{ opacity: 0, filter: 'brightness(0.9)' }}
                                animate={{ opacity: 1, filter: 'brightness(1)' }}
                                exit={{ opacity: 0, filter: 'brightness(0.9)' }}
                                transition={{ duration: 0.35, ease: 'easeOut' }}
                            >
                                <MarketingVideo
                                    className="max-w-[330px]"
                                    key={'marketing-video' + testimonial.title}
                                    src={testimonial.url}
                                    title={testimonial.title}
                                    description={testimonial.description}
                                    onEnded={() => {
                                        handleVideoState(index)
                                    }}
                                    aspectRatio={aspectRatio}
                                    isPlaying={playingIndex === index}
                                    onPlayToggle={() => {
                                        handleVideoState(index)
                                    }}
                                    setRef={(ref) => {
                                        if (ref) {
                                            videoRefs.current[index] = ref
                                        }
                                    }}
                                    thumbnail={testimonial.thumbnail}
                                />
                            </motion.div>
                        )
                    })}
                </AnimatePresence>
            </div>

            {!isDesktop && (
                <SliderNavigation
                    onChange={(index) => {
                        if (playingIndex !== null) {
                            handlePause(playingIndex)
                        }
                        setTestimonialIndex(index)
                    }}
                    activeIndex={testimonialIndex}
                    total={groupedItems.length}
                    style="dark"
                />
            )}
        </>
    )
}
